import { useTranslation } from 'react-i18next';
import type { Account, Id } from '@hero/schema';
import { formatDateMain, getAccountBalanceForViewer, getAccountCounterparty } from '@hero/core';
import { DrawerContentList } from '../../../app/Drawer/DrawerContentList';
import { Price } from '../../../app/Price';
import { AccountStatusBadge } from '../Status/AccountStatusBadge';

export interface AccountSummaryTabProps {
  account: Account;
  /** Drives the viewer-relative "Owed to you / You owe" framing. */
  viewerOrgId?: Id | string;
}

export function AccountSummaryTab({ account, viewerOrgId }: AccountSummaryTabProps) {
  const { t } = useTranslation(['m-account']);

  const counterparty = getAccountCounterparty(account, viewerOrgId);
  const counterpartyName = counterparty?.name ?? '—';
  const viewerBalance = getAccountBalanceForViewer(account, viewerOrgId);
  const balance = viewerBalance ?? account.balance;
  const isPositive = balance > 0;
  const isZero = balance === 0;
  const lastActivity = account.lastActivityAt
    ? formatDateMain({ format: 'date_time', date: account.lastActivityAt })
    : undefined;

  // The "settings" section is read-only here on purpose. Edits go
  // through the Client / Org settings surface — the Account doc
  // only stores per-pair overrides, and Hero doesn't ship a pair-
  // level settings editor yet.
  const allowAutoCapture = account.settings?.allow_auto_invoice_capture;

  return (
    <div className="flex flex-col px-5 py-2">
      <DrawerContentList
        list={[
          {
            label: t('m-account:fields.counterparty', { defaultValue: 'Counterparty' }),
            value: counterpartyName,
          },
          {
            label: t('m-account:fields.status', { defaultValue: 'Status' }),
            value: <AccountStatusBadge status={account.status} />,
          },
          {
            label: t('m-account:fields.currency', { defaultValue: 'Currency' }),
            value: account.currency,
          },
          {
            label: t(
              `m-account:balance_card.${
                isZero ? 'settled' : isPositive ? 'owed_to_you' : 'you_owe'
              }`,
              {
                defaultValue: isZero
                  ? 'Settled with {{name}}'
                  : isPositive
                    ? 'Owed to you'
                    : 'You owe',
                name: counterpartyName,
              },
            ),
            value: (
              <Price
                amount={Math.abs(balance)}
                currency={account.currency}
                className={
                  isZero ? 'text-gray-700' : isPositive ? 'text-green-600' : 'text-red-600'
                }
              />
            ),
          },
          {
            label: t('m-account:fields.last_activity', { defaultValue: 'Last activity' }),
            value: lastActivity ?? '-',
            hidden: !lastActivity,
          },
          {
            label: t('m-account:fields.allow_auto_invoice_capture', {
              defaultValue: 'Auto-capture invoices',
            }),
            value: allowAutoCapture
              ? t('m-account:fields.enabled', { defaultValue: 'Enabled' })
              : t('m-account:fields.disabled', { defaultValue: 'Disabled' }),
            hidden: allowAutoCapture == null,
          },
        ]}
      />
    </div>
  );
}
